/**
 * KeyboardHelpOverlay — press '?' to see all available keyboard shortcuts.
 * Admin-only shortcuts are shown only when the user has admin access.
 */

import { useAuth, isAdmin } from '@/lib/auth-context';

interface KeyboardHelpOverlayProps {
  open: boolean;
  onClose: () => void;
}

interface Shortcut {
  key: string;
  description: string;
  adminOnly?: boolean;
  authRequired?: boolean;
  context?: string;
}

const SHORTCUTS: Shortcut[] = [
  // Navigation
  { key: 'g', description: 'Open deck switcher — jump to any of your decks', authRequired: true },
  { key: 'l', description: 'Open card lookup — search any card and view full art' },
  { key: 'f', description: 'Go to My Decks and open Find a Home', authRequired: true },
  { key: 'm', description: 'Go to My Decks', authRequired: true },
  { key: 'p', description: 'Return to active playtest (if one is open), otherwise go to your profile', authRequired: true },
  { key: 'a', description: 'Go to Admin panel', adminOnly: true },
  // Search
  { key: 'Ctrl+K / ⌘K', description: 'Focus the search bar from anywhere' },
  // Deck view tabs
  { key: ']', description: 'Next tab (deck view)', context: 'Deck view' },
  { key: '[', description: 'Previous tab (deck view)', context: 'Deck view' },
  { key: '/', description: 'Focus card filter search (deck view)', context: 'Deck view' },
  // Universal
  { key: '?', description: 'Show this help overlay' },
  { key: 'Esc', description: 'Close any open overlay or dialog' },
  // Playtester
  { key: 'n', description: 'Advance to next phase', context: 'Playtester' },
  { key: 'N', description: 'Advance to next turn (auto-untap + untap phase)', context: 'Playtester' },
  { key: 'd', description: 'Draw a card', context: 'Playtester' },
  { key: 'Space', description: 'Tap/untap hovered card, or advance phase if nothing hovered', context: 'Playtester' },
  { key: 'f', description: 'Flip hovered double-faced card', context: 'Playtester' },
  { key: 'Shift+T', description: 'Open token panel', context: 'Playtester' },
  { key: 'Shift+L', description: 'Browse library', context: 'Playtester' },
  { key: 'Shift+G', description: 'Browse graveyard', context: 'Playtester' },
  { key: 'Shift+E', description: 'Browse exile', context: 'Playtester' },
  { key: 'Double-click (library)', description: 'Draw a card from library', context: 'Playtester' },
  { key: 'Double-click (canvas)', description: 'Pan / zoom the battlefield', context: 'Playtester' },
];

export function KeyboardHelpOverlay({ open, onClose }: KeyboardHelpOverlayProps) {
  const { user } = useAuth();
  const admin = isAdmin(user ?? null);

  if (!open) return null;

  const visible = SHORTCUTS.filter(s => {
    if (s.adminOnly && !admin) return false;
    if (s.authRequired && !user) return false;
    return true;
  });

  // Group by context
  const global = visible.filter(s => !s.context);
  const deckView = visible.filter(s => s.context === 'Deck view');
  const playtester = visible.filter(s => s.context === 'Playtester');

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] rounded-xl shadow-2xl overflow-hidden mx-4"
        style={{ background: 'var(--surface-container-high)', border: '1px solid var(--outline-variant)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-outline-variant">
          <div className="flex items-center gap-2.5">
            <span className="material-icons text-on-surface-variant" style={{ fontSize: 20 }}>keyboard</span>
            <span className="font-semibold text-on-surface">Keyboard Shortcuts</span>
          </div>
          <button
            onClick={onClose}
            className="text-on-surface-variant hover:text-on-surface transition-colors bg-transparent border-none cursor-pointer p-0.5 leading-none"
            title="Close (Esc)"
          >
            <span className="material-icons" style={{ fontSize: 18 }}>close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">
          <ShortcutGroup label="Global" shortcuts={global} />
          {deckView.length > 0 && <ShortcutGroup label="Deck View" shortcuts={deckView} />}
          {playtester.length > 0 && <ShortcutGroup label="Playtester" shortcuts={playtester} />}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-2 border-t border-outline-variant text-xs text-on-surface-variant"
          style={{ background: 'var(--surface-container)' }}
        >
          All single-key shortcuts are suppressed when typing in an input field.
          Full docs at <a href="/static/search-syntax.html" target="_blank" rel="noopener" className="text-primary hover:underline">search-syntax.html</a>
        </div>
      </div>
    </div>
  );
}

function ShortcutGroup({ label, shortcuts }: { label: string; shortcuts: Shortcut[] }) {
  if (shortcuts.length === 0) return null;
  return (
    <div className="mb-5 last:mb-0">
      <div className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant mb-2">{label}</div>
      <table className="w-full border-collapse">
        <tbody>
          {shortcuts.map(s => (
            <tr key={s.key} className="border-b border-outline-variant/40 last:border-0">
              <td className="py-2 pr-4 w-36 align-top">
                <Kbd>{s.key}</Kbd>
              </td>
              <td className="py-2 text-sm text-on-surface">
                {s.description}
                {s.adminOnly && (
                  <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 font-medium">admin</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kbd({ children }: { children: string }) {
  const keys = children.split(' / ');
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {keys.map((k, i) => (
        <span key={k}>
          <kbd
            className="inline-block text-xs px-1.5 py-0.5 rounded font-mono text-on-surface"
            style={{ background: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            {k}
          </kbd>
          {i < keys.length - 1 && <span className="text-on-surface-variant text-xs mx-0.5">/</span>}
        </span>
      ))}
    </span>
  );
}
